import type { InternalAccount, PlatformRole } from '@/graphql/staff';

/** What each platform role lets someone do, in the words the page shows. */
export const ROLES: readonly {
  role: PlatformRole;
  name: string;
  summary: string;
}[] = [
  {
    role: 'ADMIN',
    name: 'Admin',
    summary: 'Everything, including inviting people and changing their access.',
  },
  {
    role: 'ACCOUNT_MANAGER',
    name: 'Account manager',
    summary: 'Edit lenders and their documents, and brokerage settings.',
  },
  {
    role: 'PRODUCT_ENGINEER',
    name: 'Product engineer',
    summary: 'Edit lenders and brokerage configuration; view everything else.',
  },
  {
    role: 'PLATFORM_ENGINEER',
    name: 'Platform engineer',
    summary: 'Edit lenders and platform staff tooling; view everything else.',
  },
];

export function roleName(role: PlatformRole): string {
  return ROLES.find((entry) => entry.role === role)?.name ?? role;
}

export const STAFF_DOMAIN = 'lendwell.ie';

/** Only LendWell addresses may be admitted to the internal portal. */
export function isStaffEmail(email: string): boolean {
  const trimmed = email.trim().toLowerCase();
  const at = trimmed.lastIndexOf('@');
  return at > 0 && trimmed.slice(at + 1) === STAFF_DOMAIN && !/\s/.test(trimmed);
}

/** The assignments and removals that turn `from` into `to`. */
export function roleChanges(
  from: readonly PlatformRole[],
  to: readonly PlatformRole[],
): { add: PlatformRole[]; remove: PlatformRole[] } {
  return {
    add: to.filter((role) => !from.includes(role)),
    remove: from.filter((role) => !to.includes(role)),
  };
}

/** A person's label: Keystone holds no names, so only your own row has one. */
export function personLabel(
  account: InternalAccount,
  meId: string | null | undefined,
  meEmail: string | null | undefined,
): string {
  if (account.personId && account.personId === meId) {
    return meEmail ? `You (${meEmail})` : 'You';
  }
  return `Staff member ${(account.extTurnstileId ?? account.id).slice(-6)}`;
}
